// 1. Added 'useRef' to the React imports here
import { useState, useMemo, useRef, useEffect } from 'react';
import { C } from '../../../lib/tokens.js';
import { isAgentRole } from '../../../lib/utils.js';
import { useUser } from '../../../lib/UserCacheContext.jsx';
import { useLookups } from '../../../lib/LookupsContext.jsx';
import { PriorityChip, TypeChip } from '../../../components/ui/Chips.jsx';
import { Avatar } from '../../../components/ui/Avatar.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { IconText, ProgressBar, ResolutionBadge } from '../../../components/ui/Misc.jsx';
import { Modal } from '../../../components/ui/Modal.jsx';
import { useExtensionSlot } from '../../../lib/ExtensionSlotsContext.jsx';
import { useToast } from '../../../lib/ToastContext.jsx';
import { t } from '../../../lib/i18n.jsx';
import { useValidRequests, useScrollNearBottom, LoadingRow, RelatedTabs, sortRequests } from './shared.jsx';
import { BillingBadge } from '../../../components/ui/BillingMarker.jsx';

function isClosedStatus(status) {
  return status.status_category_id === 'closed' || status.name?.toLowerCase().includes('closed') || status.id === 'closed';
}

function MoveConfirmModal({ role, fromStatus, toStatus, requireMessage, onConfirm, onCancel }) {
  const [message, setMessage] = useState('');
  const [confidentialType, setConfidentialType] = useState('A');
  const [extraEnabled, setExtraEnabled] = useState(false);
  const [extraValue, setExtraValue] = useState(null);
  const [extraValid, setExtraValid] = useState(true);
  const isAgent = isAgentRole(role);
  const isClosing = requireMessage;
  // Optional module-contributed fieldset (e.g. afsp.afdc's "resolved in a product"
  // picker) - undefined when that module isn't attached, in which case this modal
  // is just the plain move/close confirmation it always was.
  const MoveResolutionsFieldset = useExtensionSlot('afdc.moveResolutionsFieldset');

  return (
    <Modal onClose={onCancel}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 15, fontWeight: 500, color: C.deepsea }}>
          {isClosing ? 'Close Request' : `Move to ${toStatus?.name ?? 'new status'}`}
        </span>
        <button onClick={onCancel} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 4 }}>
          <span className="material-icons" style={{ fontSize: 18, color: C.iron }}>close</span>
        </button>
      </div>
      <div style={{ fontSize: 13, color: C.iron }}>
        {isClosing
          ? 'A closing note is required before this request can be closed.'
          : `Change status from ${fromStatus?.name ?? 'current'} to ${toStatus?.name ?? 'new status'}?`}
      </div>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        autoFocus
        placeholder={isClosing ? 'e.g. Confirmed fixed in production.' : 'Optional note…'}
        style={{
          fontFamily: 'Roboto', fontSize: 13, padding: '8px 10px', border: `1px solid ${C.mercury}`,
          borderRadius: 4, outline: 'none', resize: 'vertical', lineHeight: 1.5,
          color: C.fg, background: C.platinum, boxSizing: 'border-box', width: '100%',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, color: C.iron }}>Visibility</span>
        <select
          value={confidentialType}
          onChange={(e) => setConfidentialType(e.target.value)}
          style={{
            fontFamily: 'Roboto', fontSize: 12, padding: '5px 8px', borderRadius: 4,
            border: `1px solid ${C.mercury}`, background: C.platinum, color: C.fg, outline: 'none',
            cursor: 'pointer',
          }}
        >
          <option value="A">Public Information</option>
          <option value="C">Partner Confidential</option>
          {isAgent && <option value="I">Internal</option>}
        </select>
      </div>

      {isAgent && MoveResolutionsFieldset && (
        <MoveResolutionsFieldset
          enabled={extraEnabled} onEnabledChange={setExtraEnabled}
          value={extraValue} onChange={setExtraValue}
          onValidChange={setExtraValid}
        />
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button kind="outline" onClick={onCancel}>Cancel</Button>
        <Button kind="primary" disabled={(isClosing && !message.trim()) || (extraEnabled && !extraValid)}
          onClick={() => onConfirm(message.trim(), confidentialType, extraEnabled ? extraValue : null)}>
          {isClosing ? 'Mark as Resolved' : 'Confirm'}
        </Button>
      </div>
    </Modal>
  );
}

export function KanbanBoard({ requests, onOpen, onPeek, density, showPriority, role, onMove, hasMore, onLoadMore, loadingMore, categoryId, sort: boardSort = 'default' }) {
  const { statuses } = useLookups();
  const { addToast } = useToast();
  const validRequests = useValidRequests(requests);
  const [pendingMove, setPendingMove] = useState(null); // { id, statusId, fromStatus, toStatus }
  // Optional module-contributed persist function for whatever MoveResolutionsFieldset
  // collected (see MoveConfirmModal) - undefined when that module isn't attached.
  const persistMoveExtra = useExtensionSlot('afdc.persistMoveResolutions');

  const boardStatuses = useMemo(() => {
    if (!categoryId || categoryId === '0') return statuses;
    const filtered = statuses.filter(s => s.status_category_id === categoryId);
    return filtered.length > 0 ? filtered : statuses;
  }, [statuses, categoryId]);

  const grouped = boardStatuses.map(s => ({
    status: s,
    items: sortRequests(validRequests.filter(r => r.status === s.id), boardSort),
  }));

  const isCustomer = role === 'customer';
  const visibleColumns = isCustomer ? grouped.filter(col => col.items.length > 0) : grouped;

  function guardedMove(id, targetStatusId) {
    const item = validRequests.find(r => r.id === id);
    const targetStatus = boardStatuses.find(s => s.id === targetStatusId);
    if (!item || item.status === targetStatusId) return;
    const fromStatus = boardStatuses.find(s => s.id === item.status);
    setPendingMove({ id, statusId: targetStatusId, fromStatus, toStatus: targetStatus });
  }

  const effectiveOnMove = isCustomer ? undefined : guardedMove;

  const knownStatusIds = new Set(boardStatuses.map(s => s.id));
  const unknownItems = sortRequests(validRequests.filter(r => !knownStatusIds.has(r.status)), boardSort);
  const unknownCol = unknownItems.length > 0
    ? [{ status: { id: '__unknown', name: 'Unknown', color: C.silver }, items: unknownItems }]
    : [];

  const allColumns = [...visibleColumns, ...unknownCol];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ display: 'flex', gap: 12, padding: '12px 24px 16px', overflowX: 'auto', flex: 1, alignItems: 'stretch', minHeight: 0 }}>
        {allColumns.map(col => {
          const isClosedColumn = isClosedStatus(col.status);
          const onNearBottom = isClosedColumn && hasMore && !loadingMore ? onLoadMore : undefined;

          return (
            <KanbanColumn key={col.status.id} status={col.status} items={col.items}
              onOpen={onOpen} onPeek={onPeek} density={density} showPriority={showPriority} role={role}
              onMove={col.status.id === '__unknown' ? undefined : effectiveOnMove}
              onNearBottom={onNearBottom} loadingMore={loadingMore} />
          );
        })}
      </div>

      {pendingMove && (
        <MoveConfirmModal
          role={role}
          fromStatus={pendingMove.fromStatus}
          toStatus={pendingMove.toStatus}
          requireMessage={isClosedStatus(pendingMove.toStatus)}
          onCancel={() => setPendingMove(null)}
          onConfirm={(message, confidentialType, extra) => {
             // Sent without a message too: Internal is what keeps the status change email from going out.
            onMove?.(pendingMove.id, pendingMove.statusId, message || undefined, confidentialType);
            if (extra != null && persistMoveExtra) {
              persistMoveExtra(pendingMove.id, extra)
                .catch(() => addToast('Status updated, but one or more additional details could not be saved.'));
            }
            setPendingMove(null);
          }}
        />
      )}
    </div>
  );
}

function KanbanColumn({ status, items, onOpen, onPeek, density, showPriority, role, onMove, onNearBottom, loadingMore }) {
  const [dragOver, setDragOver] = useState(false);
  const [isManualCollapsed, setIsManualCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const canDrag = !!onMove;
  const scrollRef = useRef(null);
  const columnRef = useRef(null);
  const dragOverTimer = useRef(null);
  useScrollNearBottom(scrollRef, onNearBottom, !!onNearBottom);

  function clearDragOverTimer() {
    if (dragOverTimer.current) {
      clearTimeout(dragOverTimer.current);
      dragOverTimer.current = null;
    }
  }

  function scheduleDragOver() {
    if (dragOverTimer.current || dragOver) return;
    dragOverTimer.current = setTimeout(() => {
      dragOverTimer.current = null;
      setDragOver(true);
    }, 350);
  }

  function cancelDragOver() {
    clearDragOverTimer();
    setDragOver(false);
  }

  useEffect(() => clearDragOverTimer, []);

  const isAgentAutoCollapse = role === 'agent' && items.length === 0 && !dragOver;
  const collapsed = (isManualCollapsed || isAgentAutoCollapse) && !dragOver;

  function expandAndScroll() {
    setIsManualCollapsed(false);
    setTimeout(() => {
      columnRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }, 220);
  }

  return (
    <div
      ref={columnRef}
      onDragOver={canDrag ? (e => { e.preventDefault(); scheduleDragOver(); }) : undefined}
      onDragLeave={canDrag ? (e => { if (!e.currentTarget.contains(e.relatedTarget)) cancelDragOver(); }) : undefined}
      onDrop={canDrag ? (e => { e.preventDefault(); cancelDragOver(); const id = e.dataTransfer.getData('text/plain'); if (id) onMove(id, status.id); }) : undefined}
      style={{
        flex: 'none',
        width: collapsed ? 44 : 320,
        display: 'flex', flexDirection: 'column',
        background: dragOver ? C.rowActive : C.platinum,
        border: `1px solid ${dragOver ? C.liquid : C.mercury}`, borderRadius: 6, minHeight: 0,
        overflow: 'hidden',
        transition: 'width 200ms ease, background 120ms, border-color 120ms',
      }}>
      {collapsed ? (
        <div
          onClick={expandAndScroll}
          title="Expand column"
          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '14px 0', gap: 10, cursor: 'pointer' }}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: status.color, flex: 'none' }} />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 16, flex: 'none' }}>
            <span style={{
              display: 'inline-block',
              width: 6, height: 6,
              borderRight: `2px solid ${C.iron}`,
              borderTop: `2px solid ${C.iron}`,
              transform: 'rotate(45deg)',
            }} />
          </div>

          <span style={{
            fontSize: 10, fontWeight: 600, color: items.length > 0 ? C.fg : C.silver,
            padding: '2px 6px', background: items.length > 0 ? C.tin : 'transparent',
            borderRadius: 999, border: `1px solid ${items.length > 0 ? C.mercury : 'transparent'}`,
            transition: 'all 120ms',
            flex: 'none'
          }}>
            {items.length}
          </span>

          <span style={{
            fontSize: 12, fontWeight: 500, color: C.iron,
            writingMode: 'vertical-rl', transform: 'rotate(180deg)',
            overflow: 'hidden', whiteSpace: 'nowrap', userSelect: 'none',
            marginTop: 4
          }}>/ {status.name}</span>

          <div style={{ flex: 1 }} />
        </div>
      ) : (
        <>
          <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${C.mercury}` }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: status.color }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: C.fg, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={status.name}>{status.name}</span>
            <span style={{ fontSize: 11, color: C.iron, fontWeight: 500, padding: '1px 6px', background: C.tin, borderRadius: 999, border: `1px solid ${C.mercury}` }}>{items.length}</span>
            <div style={{ flex: 1 }} />

            <button
              onClick={() => setIsManualCollapsed(true)}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              title="Collapse column"
              style={{
                background: isHovered ? C.tin : 'none',
                border: 'none',
                cursor: 'pointer',
                width: 24,
                height: 24,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                userSelect: 'none',
                transition: 'background 120ms',
              }}
            >
              <span style={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderLeft: `2px solid ${isHovered ? C.fg : C.iron}`,
                borderBottom: `2px solid ${isHovered ? C.fg : C.iron}`,
                transform: isHovered ? 'rotate(45deg) translateX(-1px)' : 'rotate(45deg)',
                transition: 'border-color 120ms, transform 120ms ease',
              }} />
            </button>
          </div>
          {/* 3. CHANGED: Passed scrollRef here instead of the undefined 'ref' variable */}
          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.length === 0 ? (
              <div style={{ padding: '24px 12px', fontSize: 12, textAlign: 'center', fontStyle: 'italic',
                color: dragOver ? C.liquid : C.silver, fontWeight: dragOver ? 500 : 400 }}>
                {dragOver ? 'Drop here' : 'No requests in this status.'}
              </div>
            ) : items.filter(Boolean).map(req => (
              <RequestCard key={req.id} req={req} onOpen={onOpen} onPeek={onPeek} compact={density === 'compact'} showPriority={showPriority} canDrag={canDrag} />
            ))}
            {loadingMore && !!onNearBottom && items.length > 0 && <LoadingRow />}
          </div>
        </>
      )}
    </div>
  );
}

function RequestCard({ req, onOpen, onPeek, compact, showPriority, canDrag }) {
  const [hover, setHover] = useState(false);
  const [titleHover, setTitleHover] = useState(false);
  const [docHover, setDocHover] = useState(false);
  const assignee = useUser(req?.assigneeId);
  const reporter = useUser(req?.reporterId);
  // undefined (not just an empty array) when afsp.afts isn't installed - nothing
  // to render either way, but this keeps a deployment without it looking exactly
  // like it does today, not a muted "no clusters" placeholder on every card.
  const ClusterBadge = useExtensionSlot('request.card.clusterBadge');

  if (!req) return null;

  function openFull(e) {
    e.stopPropagation();
    onOpen(req);
  }

  return (
    <div draggable={canDrag} onDragStart={canDrag ? (e => e.dataTransfer.setData('text/plain', req.id)) : undefined}
      onClick={() => (onPeek ?? onOpen)(req)}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        background: C.tin, border: `1px solid ${hover ? C.silver : C.mercury}`, borderRadius: 6,
        padding: compact ? 10 : 12,
        boxShadow: hover ? '0 2px 6px rgba(34,52,105,0.10)' : '0 1px 2px rgba(34,52,105,0.04)',
        cursor: 'pointer', transition: 'all 180ms', display: 'flex', flexDirection: 'column', gap: compact ? 6 : 8,
        flexShrink: 0,
      }}>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        rowGap: 4,
        fontSize: 11,
        color: C.iron,
        fontFamily: 'Roboto Mono'
      }}>
        <TypeChip id={req.type} size="sm" readOnly />
        <span style={{ flex: 1 }} />
        <BillingBadge req={req} />
      </div>

      <div
        onClick={openFull}
        onMouseEnter={() => setTitleHover(true)} onMouseLeave={() => setTitleHover(false)}
        title="Open request"
        style={{
          fontSize: compact ? 12 : 13, color: C.fg, fontWeight: 500, lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: compact ? 2 : 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          textDecoration: titleHover ? 'underline' : 'none', width: 'fit-content',
        }}>{req.summary}</div>
      <RelatedTabs related={req.related} onOpen={onOpen} />
      {!compact && req.docNumber ? (
        <div
          onClick={openFull}
          onMouseEnter={() => setDocHover(true)} onMouseLeave={() => setDocHover(false)}
          title="Open request"
          style={{
            display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.iron,
            width: 'fit-content', textDecoration: docHover ? 'underline' : 'none',
          }}>
          <span className="material-icons" style={{ fontSize: 13 }}>description</span>
          <span style={{ fontFamily: 'Roboto Mono' }}>{req.docNumber}</span>
        </div>
      ) : null}
      {req.progress > 0 && req.progress < 100 ? (
        <ProgressBar value={req.progress} color={req.progress > 75 ? C.patina : C.liquid} />
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.iron, marginTop: 2, minWidth: 0 }}>
        {showPriority ? <PriorityChip id={req.priority} size="sm" readOnly /> : null}
        <span style={{ flex: 1 }} />
        {req.comments > 0    ? <IconText icon="forum"       size={11}>{req.comments}</IconText>    : null}
        {req.attachmentCount > 0 ? <IconText icon="attach_file" size={11}>{req.attachmentCount}</IconText> : null}
        <ResolutionBadge resolutions={req.resolutions} size={11} />
        {ClusterBadge && req.clusters?.length > 0 ? (
          <div style={{ minWidth: 0, overflow: 'hidden' }}><ClusterBadge clusters={req.clusters} /></div>
        ) : null}
        <div style={{ flexShrink: 0 }}><Avatar user={assignee || reporter} size={20} /></div>
      </div>
    </div>
  );
}